import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../../redux/rootReducer";
import { AppThunk } from "../../../../redux/store";
import TeamsService from "../services/teamsService";
import { ITeam, ITeamResponse } from "../../../../types/ITeam";
import makeQueryString from "../../../../helpers/urlBuilder.helpers";

export interface TeamsError {
  message: string;
}

export interface TeamsState {
  teams: ITeam[];
  fetchingTeamsInProgress: boolean;
  fetchingTeamsError: TeamsError;
  currentTeam: ITeam;
  fetchingCurrentTeamsInProgress: boolean;
  fetchingCurrentTeamError: TeamsError;
  offset: number;
  perPage: number;
  currentPage: number;
  pageCount: number | null;
  saveTeamStateInProgress: boolean;
  saveTeamStateError: TeamsError;
  teamInfoIsNotFilled: boolean;
}

export const initialState: TeamsState = {
  teams: [],
  fetchingTeamsInProgress: false,
  fetchingTeamsError: null,
  currentTeam: null,
  fetchingCurrentTeamsInProgress: false,
  fetchingCurrentTeamError: null,
  offset: 0,
  perPage: 50,
  currentPage: 0,
  pageCount: 0,
  saveTeamStateInProgress: false,
  saveTeamStateError: null,
  teamInfoIsNotFilled: null,
};

export const teamsSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    setTeamsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingTeamsInProgress = payload;
    },
    setTeamsSuccess: (state, { payload }: PayloadAction<ITeamResponse>) => {
      state.teams = payload.teams;
      state.fetchingTeamsInProgress = false;
      state.pageCount = Math.ceil(payload.count / state.perPage);
      state.fetchingTeamsError = null;
    },
    setTeamsFailed: (state, { payload }: PayloadAction<TeamsError>) => {
      state.fetchingTeamsError = payload;
    },
    setCurrentTeamLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.teamInfoIsNotFilled = null;
      state.fetchingCurrentTeamsInProgress = payload;
    },
    setCurrentTeamSuccess: (state, { payload }: PayloadAction<ITeam>) => {
      state.currentTeam = payload;
      state.teams.find((team, index) => {
        if (team.id === payload.id) {
          state.teams[index] = payload;
        }
      });
      if (
        !payload.fieldOfOperations ||
        !payload.regionOfOperations ||
        payload.isNgo == null ||
        !payload.foundationYear ||
        !payload.quantityOfMembers ||
        !payload.quantityOfInstitutionalMembers ||
        !payload.quantityOfEmployees ||
        !payload.about ||
        !payload.email ||
        !payload.contactNumber
      ) {
        state.teamInfoIsNotFilled = true;
      } else {
        state.teamInfoIsNotFilled = false;
      }

      state.fetchingCurrentTeamError = null;
    },
    setCurrentTeamsFailed: (state, { payload }: PayloadAction<TeamsError>) => {
      state.fetchingCurrentTeamError = payload;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setOffset: (state, { payload }: PayloadAction<number>) => {
      state.offset = payload;
    },
    setSaveTeamStateLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.saveTeamStateInProgress = payload;
    },
    setSaveTeamStateSuccess: (state) => {
      state.saveTeamStateInProgress = false;
      state.saveTeamStateError = null;
    },
    setSaveTeamStateFailed: (state, { payload }: PayloadAction<TeamsError>) => {
      state.saveTeamStateError = payload;
    },
  },
});

export const {
  setTeamsLoading,
  setTeamsSuccess,
  setTeamsFailed,
  setCurrentTeamLoading,
  setCurrentTeamSuccess,
  setCurrentTeamsFailed,
  setCurrentPage,
  setOffset,
  setSaveTeamStateLoading,
  setSaveTeamStateSuccess,
  setSaveTeamStateFailed,
} = teamsSlice.actions;

type IFetchQueryOptions = { searchText?: string; pageNumber?: number };

export const setCurrentPageAction =
  (currentPage: number): AppThunk =>
  async (dispatch) => {
    dispatch(setCurrentPage(currentPage));
  };

export const setOffsetAction =
  (offset: number): AppThunk =>
  async (dispatch) => {
    dispatch(setOffset(offset));
  };

export const fetchTeams =
  (query?: IFetchQueryOptions): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setTeamsLoading(true));
      const { data: teamsResponse } = await TeamsService.getAll(
        makeQueryString({ ...query, perPage: getState().teams.perPage })
      );
      dispatch(setTeamsSuccess(teamsResponse));
    } catch (error) {
      dispatch(setTeamsFailed(error));
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

export const fetchTeam =
  (teamId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setCurrentTeamLoading(true));
      const { data: team } = await TeamsService.getOne(teamId);

      dispatch(setCurrentTeamSuccess(team));
    } catch (error) {
      dispatch(setCurrentTeamsFailed(error));
    } finally {
      dispatch(setCurrentTeamLoading(false));
    }
  };

export const saveTeamState =
  (teamId: number, payload: Object): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setSaveTeamStateLoading(true));
      const updatedTeamResponse: any = await TeamsService.saveTeamState(
        teamId,
        payload
      );
      dispatch(setCurrentTeamSuccess(updatedTeamResponse));
      dispatch(setSaveTeamStateSuccess());
    } catch (error) {
      dispatch(setSaveTeamStateFailed(error));
    } finally {
      dispatch(setSaveTeamStateLoading(false));
    }
  };

export const teamsSelector = (state: RootState) => state.teams;
export default teamsSlice.reducer;
